import * as Yup from 'yup';
import { paymentMethod } from '@ourbranch/lookups';

import { umbrellaValidationSchema } from './umbrella.validation-schema';
import { connectedHomeValidationSchema } from './connected-home.validation-schema';
import { validHomePurchaseDate, addressBlock } from 'common/helpers/yup-helpers';

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);

const min = (minVal) => (val) => val >= minVal;

export const condoValidationSchema = ({
  policy,
  policies,
  savedSegment,
  autoPolicyDetails,
  isAdvancedConnectedHome
}) => {
  const baseSchema = Yup.object().shape({
    condo: Yup.object().shape({
      purchaseDate: Yup.string().validHomePurchaseDate('condo.purchaseDate', false),
      yearBuilt: Yup.number().test('yearBuiltMin', 'Year built must be higher than 1899', min(1900)),
      condoLocation: addressBlock('Condo address', {
        address2: Yup.string()
          .nullable()
          .test('required-in-condo', `Condo's apartment or unit number is required`, (value, { options }) => {
            if (!options.context.condo.noUnitNumber) {
              return !!value;
            }
            return true;
          })
      }),
      mortgageDetails: Yup.array(
        Yup.object().shape({
          mortgageHolderName: Yup.string()
            .trim()
            .required('Required. If trying to remove this mortgage, use the Trash button.'),
          mortgageHolderAddress: addressBlock('Mortgage holder address'),
          mortgageHolderEmail: Yup.string().trim().email('Please use a valid email').nullable(),
          mortgageHolderFax: Yup.string().trim().nullable(),
          loanNumber: Yup.string().trim().required('Mortgage loan number is required')
        })
      )
        .nullable()
        .test(
          'atLeastOneRequired',
          'At least one mortgage details required because payment method is mortgage',
          (mortgageList) => {
            if (policy.paymentMethod === paymentMethod.Escrow) {
              return mortgageList?.length > 0;
            }
            return true;
          }
        )
    }),
    scheduledPersonalProperty: Yup.object()
      .shape({
        items: Yup.array(Yup.object()),
        deductible: Yup.mixed().test('deductible', 'SPP Deductible is required', function (value) {
          return !(this.parent.items && this.parent.items.length && !value);
        })
      })
      .nullable(),
    connectedHome: connectedHomeValidationSchema(isAdvancedConnectedHome),
    condoCoverage: Yup.object().shape({
      coverageX: Yup.number().test(
        'umbrellaLiabilityMinimum',
        'Umbrella coverage requires at least $300K in personal liability.',
        function (value) {
          if (!this.options.context?.includeUmbrella) {
            return true;
          }
          return (value || 0) >= 300000;
        }
      ),
      coverageC: Yup.number()
        .test(
          'coverageC min limit',
          'Personal Property Limit cannot be lower than $10K.',
          (currentCoverageC) => currentCoverageC >= 10000
        )
        .test(
          'coverageC max limit',
          'Personal Property Limit value cannot be higher than $500K',
          (currentCoverageC) => currentCoverageC <= 500000
        ),
      coverageA: Yup.number()
        .test(
          'coverageA min limit',
          'Building Property Protection cannot be lower than $50K.',
          (currentCoverageA) => currentCoverageA >= 50000
        )
        .test(
          'coverageA max limit',
          'Dwelling limit value cannot be higher than 200% RCE',
          function (
            currentCoverageA,
            {
              options: {
                context: { condo }
              }
            }
          ) {
            const max = condo.replacementCostEstimate.total * 2.0;
            if (currentCoverageA > max) {
              return this.createError({
                message: `Building Property Protection cannot exceed $${max} (200% RCE)`
              });
            }
            return true;
          }
        )
    }),
    people: Yup.array(
      Yup.object().shape({
        firstName: Yup.string().trim().required('First Name is required'),
        lastName: Yup.string().trim().required('Last Name is required')
      })
    )
  });

  return baseSchema.concat(umbrellaValidationSchema({ policy, policies, savedSegment, autoPolicyDetails }));
};
